import { z } from "zod";
import { InventoryTypeSchema } from "./configuration.js";

/**
 * Every POST from a customer-account Lambda to the central ingestion API
 * uses this envelope. `friendly_name` / `aws_account_number` are
 * informational only — the ingestion Lambda resolves the authoritative
 * `environment_id` from the caller's API key and cross-checks these fields
 * against the stored record rather than trusting them. See
 * documentation/design.md.
 *
 * ADR-0005: nothing destination-specific (case IDs, ticket IDs, backend
 * names) may ever appear here. This shape is baked into every deployed
 * customer stack and cannot be updated once it is in their account.
 */
export const EventTypeSchema = z.enum([
  "cloudwatch_alarm",
  "trusted_advisor",
  "support_case",
  "service_quota",
  "concurrency_metrics",
  "journey_rollup",
  "connect_inventory",
  "heartbeat",
  "test",
]);
export type EventType = z.infer<typeof EventTypeSchema>;

export const SeveritySchema = z.enum(["critical", "warning", "info"]);
export type Severity = z.infer<typeof SeveritySchema>;

export const AlarmStateSchema = z.enum(["ALARM", "OK", "INSUFFICIENT_DATA"]);
export type AlarmState = z.infer<typeof AlarmStateSchema>;

export const CloudWatchAlarmPayloadSchema = z.object({
  alarmName: z.string().min(1),
  alarmArn: z.string().min(1),
  region: z.string().min(1),
  state: AlarmStateSchema,
  previousState: AlarmStateSchema.optional(),
  // CloudWatch's own format, e.g. "2026-08-29T11:59:30.000+0000": an offset without the colon zod's ISO check requires.
  stateChangeTime: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})$/, "CloudWatch timestamp"),
  reason: z.string().max(2048).optional(),
  metricName: z.string().optional(),
  namespace: z.string().optional(),
  dimensions: z.record(z.string(), z.string()).optional(),
  /** Best single identifier for the affected resource, e.g. a Connect instance ID. */
  resource: z.string().optional(),
});
export type CloudWatchAlarmPayload = z.infer<typeof CloudWatchAlarmPayloadSchema>;

export const TrustedAdvisorPayloadSchema = z.object({
  checkId: z.string(),
  checkName: z.string(),
  category: z.string().optional(),
  status: z.enum(["ok", "warning", "error", "not_available"]),
  flaggedResourceCount: z.number().int().nonnegative().optional(),
  /** Deterministic per (check, status, flagged count): a re-poll with no change is a duplicate, not a new finding. */
  observedAt: z.string().datetime({ offset: true }).optional(),
});
export type TrustedAdvisorPayload = z.infer<typeof TrustedAdvisorPayloadSchema>;

export const SupportCasePayloadSchema = z.object({
  caseId: z.string(),
  displayId: z.string().optional(),
  subject: z.string(),
  status: z.string(),
  severity: z.string().optional(),
  service: z.string().optional(),
  timeCreated: z.string().optional(),
  lastUpdated: z.string(),
  lastCommunications: z
    .array(
      z.object({
        body: z.string(),
        submittedBy: z.string().optional(),
        timeCreated: z.string(),
      }),
    )
    .max(2),
  /** Poll-level marker so the router can replace the cache: all cases from one poll share observedAt. */
  observedAt: z.string().datetime({ offset: true }).optional(),
  chunk: z.object({ index: z.number().int().nonnegative(), count: z.number().int().positive() }).optional(),
});
export type SupportCasePayload = z.infer<typeof SupportCasePayloadSchema>;

/**
 * How a quota's current usage was measured. Mirrors the three mechanisms
 * in design.md "Service quota polling mechanism"; `unavailable` means the
 * limit is known but nothing measures usage for it.
 */
export const QuotaUsageSourceSchema = z.enum([
  "resource_count",
  "cloudwatch_usage",
  "native_metric",
  "unavailable",
  /** A throughput limit (N per second): no usage is measured, and no increase is ever recommended - the remedy is caching or queueing. */
  "rate",
]);
export type QuotaUsageSource = z.infer<typeof QuotaUsageSourceSchema>;

export const ServiceQuotaItemSchema = z.object({
  quotaCode: z.string().min(1),
  quotaName: z.string().min(1),
  value: z.number(),
  unit: z.string().optional(),
  adjustable: z.boolean(),
  usage: z.number().nonnegative().optional(),
  usageSource: QuotaUsageSourceSchema,
});
export type ServiceQuotaItem = z.infer<typeof ServiceQuotaItemSchema>;

/**
 * A poll run is split into chunks (WAF body limit); all chunks share
 * `observedAt`, and `chunk` tells the router which one is last so it can
 * drop rows this run did not refresh (always-replace semantics).
 */
export const ServiceQuotaPayloadSchema = z.object({
  serviceCode: z.string().min(1),
  observedAt: z.string().datetime({ offset: true }),
  quotas: z.array(ServiceQuotaItemSchema).max(500),
  chunk: z.object({ index: z.number().int().nonnegative(), count: z.number().int().positive() }).optional(),
});
export type ServiceQuotaPayload = z.infer<typeof ServiceQuotaPayloadSchema>;

/**
 * ADR-0004: a compact metric series pushed on the quota poller's
 * schedule. ~72 points per metric per 6h run at 5-minute resolution.
 */
/**
 * The instance-level concurrency metrics Amazon Connect emits, by their
 * real names (adminguide monitoring-cloudwatch.html): the voice pair is
 * ConcurrentCalls / ConcurrentCallsPercentage, not "ConcurrentActive…".
 * Percentages are pushed as 0-100 - the poller scales the calls one,
 * which Connect emits as a 0-1 fraction, so the portal never has to know.
 */
export const ConcurrencyMetricSchema = z.enum([
  "ConcurrentCalls",
  "ConcurrentCallsPercentage",
  "ConcurrentActiveChats",
  "ConcurrentActiveChatsPercentage",
  "ConcurrentTasks",
  "ConcurrentTasksPercentage",
  "ConcurrentEmails",
  "ConcurrentEmailsPercentage",
]);
export type ConcurrencyMetric = z.infer<typeof ConcurrencyMetricSchema>;

export const ConcurrencyMetricsPayloadSchema = z.object({
  observedAt: z.string().datetime({ offset: true }),
  points: z
    .array(
      z.object({
        metric: ConcurrencyMetricSchema,
        timestamp: z.string().datetime({ offset: true }),
        value: z.number().nonnegative(),
      }),
    )
    .max(1000),
});
export type ConcurrencyMetricsPayload = z.infer<typeof ConcurrencyMetricsPayloadSchema>;

/**
 * ADR-0003 Track A: pre-aggregated contact-flow edges for one window and
 * one filter slice. Nodes are keyed on flow + block id, never block name.
 */
export const JourneyDimensionSchema = z.enum(["all", "channel", "queue", "dnis", "flow"]);
export type JourneyDimension = z.infer<typeof JourneyDimensionSchema>;

export const JourneyEdgeSchema = z.object({
  from: z.string().min(1),
  to: z.string().min(1),
  fromLabel: z.string().min(1),
  toLabel: z.string().min(1),
  /** The branch the source block took to reach the target - a pressed key, "Timeout", "Error" - when the log says. */
  branch: z.string().max(64).optional(),
  count: z.number().int().nonnegative(),
  avgDwellMs: z.number().nonnegative().optional(),
  errorCount: z.number().int().nonnegative(),
  /** A few recent contacts on this edge: the bridge from the Sankey into the single-call view (requirements.md section 8). */
  sampleContactIds: z.array(z.string()).max(20).optional(),
});
export type JourneyEdge = z.infer<typeof JourneyEdgeSchema>;

export const JourneyRollupPayloadSchema = z.object({
  windowStart: z.string().datetime({ offset: true }),
  windowEnd: z.string().datetime({ offset: true }),
  dimension: JourneyDimensionSchema,
  /** Slice value for the dimension, e.g. "VOICE", "Sales", "+18005551234"; "*" for `all`. */
  slice: z.string().min(1),
  contactCount: z.number().int().nonnegative(),
  edges: z.array(JourneyEdgeSchema).max(2000),
  chunk: z.object({ index: z.number().int().nonnegative(), count: z.number().int().positive() }).optional(),
});
export type JourneyRollupPayload = z.infer<typeof JourneyRollupPayloadSchema>;

/**
 * What this environment's Connect instance actually has (requirements.md
 * 10.2), pushed on a schedule so the portal can offer real, human-readable
 * names when a profile is authored. Configuration metadata, not CX
 * content (ADR-0006) - names and ARNs, never a recording or a transcript.
 */
export const ConnectInventoryPayloadSchema = z.object({
  observedAt: z.string().datetime({ offset: true }),
  items: z
    .array(
      z.object({
        type: InventoryTypeSchema,
        name: z.string().min(1).max(256),
        arn: z.string().min(1).max(2048),
        resourceId: z.string().max(256).optional(),
      }),
    )
    .max(500),
  /** An instance with thousands of queues arrives across several pushes. */
  chunk: z.object({ index: z.number().int().nonnegative(), count: z.number().int().positive() }).optional(),
});
export type ConnectInventoryPayload = z.infer<typeof ConnectInventoryPayloadSchema>;

export const TemplateComponentSchema = z.enum([
  "EnableConnectAlarms",
  "EnableLexAlarms",
  "QuotaIncreaseRequestRole",
  "JourneyInspectorRole",
  "FlowConfiguration",
]);
export type TemplateComponent = z.infer<typeof TemplateComponentSchema>;

export const CrossAccountSchema = z.object({
  quota_increase_role_arn: z.string().regex(/^arn:aws[\w-]*:iam::\d{12}:role\/.+$/).optional(),
  journey_inspector_role_arn: z.string().regex(/^arn:aws[\w-]*:iam::\d{12}:role\/.+$/).optional(),
  journey_cache_table: z.string().min(3).max(255).optional(),
});
export type CrossAccount = z.infer<typeof CrossAccountSchema>;

/** See design.md "Heartbeat and template drift". Updates `Environments` only, never `Events`. */
export const HeartbeatPayloadSchema = z.object({
  template_version: z.string().min(1).max(64),
  stack_id: z.string().min(1),
  region: z.string().min(1),
  enabled_components: z.array(TemplateComponentSchema),
  /** Undelivered alarm events sitting in the forwarder's dead-letter queue. */
  forwarder_dlq_depth: z.number().int().nonnegative().optional(),
  /** ADR-0003 Track B inputs, declared in the customer's template and reported here so central knows where to look. */
  journey_config: z
    .object({
      contact_flow_log_group: z.string().optional(),
      log_groups: z.array(z.string()).max(50).optional(),
      connect_instance_id: z.string().optional(),
      /** Hours the customer chose to keep a reconstructed call in their own cache table (ADR-0006). */
      cache_retention_hours: z.number().int().positive().max(168).optional(),
    })
    .optional(),
  /**
   * What the stack created for the opt-ins central reaches into their
   * account for. Names carry the stack name so several deployments can
   * share one account (one per Connect instance, #93); central assumes
   * and reads exactly what is reported here, never a composed name.
   */
  cross_account: CrossAccountSchema.optional(),
});
export type HeartbeatPayload = z.infer<typeof HeartbeatPayloadSchema>;

export const TestPayloadSchema = z.object({
  triggeredBy: z.literal("cloudformation-custom-resource"),
  template_version: z.string().min(1).max(64).optional(),
});
export type TestPayload = z.infer<typeof TestPayloadSchema>;

const BaseEnvelopeSchema = z.object({
  /**
   * Deterministic, not random: hash of aws_account_number + the source
   * identifier (alarm ARN, check ID, ...) + the source's own state-change
   * timestamp. The ingestion Lambda writes under a conditional put on
   * this, so SNS redelivery and forwarder retries collapse onto one item.
   */
  event_id: z.string().regex(/^[a-f0-9]{32}$/),
  friendly_name: z.string().min(1).max(256),
  aws_account_number: z.string().regex(/^\d{12}$/),
  /**
   * Set in the customer account, where the template knows which baseline
   * alarm fired. Absent for alarms a customer wired to the topic
   * themselves; the router falls back to a central lookup.
   */
  severity: SeveritySchema.optional(),
  timestamp: z.string().datetime({ offset: true }),
});

export const EventEnvelopeSchema = z.discriminatedUnion("type", [
  BaseEnvelopeSchema.extend({ type: z.literal("cloudwatch_alarm"), payload: CloudWatchAlarmPayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("trusted_advisor"), payload: TrustedAdvisorPayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("support_case"), payload: SupportCasePayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("service_quota"), payload: ServiceQuotaPayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("concurrency_metrics"), payload: ConcurrencyMetricsPayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("journey_rollup"), payload: JourneyRollupPayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("connect_inventory"), payload: ConnectInventoryPayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("heartbeat"), payload: HeartbeatPayloadSchema }),
  BaseEnvelopeSchema.extend({ type: z.literal("test"), payload: TestPayloadSchema }),
]);
export type EventEnvelope = z.infer<typeof EventEnvelopeSchema>;

/** Shape of every error the ingestion API returns (CLAUDE.md section 12). */
export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
    request_id: z.string(),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

export const IngestAcceptedSchema = z.object({
  event_id: z.string(),
  request_id: z.string(),
  duplicate: z.boolean(),
});
export type IngestAccepted = z.infer<typeof IngestAcceptedSchema>;
